/**
 * DailyBriefing — the "Mission du jour" block.
 *
 * Renders Inea's greeting + 3 concrete actions. Each action card links to the
 * relevant module (and pre-selects the prospect when applicable).
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Sunrise,
  UserCheck,
  BellRing,
  CalendarDays,
  UserSearch,
  Loader2,
  RefreshCw,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useCoach, type ActionType, type BriefingAction } from '@/hooks/useCoach';

const ACTION_ICONS: Record<ActionType, typeof UserCheck> = {
  follow_up: BellRing,
  prospect: UserCheck,
  content: CalendarDays,
  audit: UserSearch,
};

const ACTION_TINTS: Record<ActionType, string> = {
  follow_up: '#f59e0b',
  prospect: '#ec4899',
  content: '#a855f7',
  audit: '#3b82f6',
};

interface DailyBriefingProps {
  /** Compact mode (used inside the dashboard hero). */
  compact?: boolean;
}

export function DailyBriefing({ compact = false }: DailyBriefingProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { briefing: b, isLoading, regenerate } = useCoach();

  if (isLoading) {
    return (
      <div className="card-hi flex items-center justify-center rounded-[28px] p-10">
        <Loader2 className="animate-spin text-pink" size={24} />
      </div>
    );
  }

  if (!b) return null;

  return (
    <div className="relative overflow-hidden rounded-[28px]">
      <div
        className="pointer-events-none absolute -right-20 -top-24 h-[340px] w-[340px] rounded-full opacity-50 blur-3xl"
        style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-24 -left-16 h-[300px] w-[300px] rounded-full opacity-40 blur-3xl"
        style={{ background: 'radial-gradient(circle, #3b82f6, transparent 70%)' }}
      />
      <div className="card-hi relative rounded-[28px] p-7 sm:p-8">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div className="flex-1">
            <span className="kicker text-pink">
              <Sunrise size={12} />
              {t('briefing.kicker')} · {b.energy}
            </span>
            <p className="mt-2 max-w-2xl text-[15px] leading-[1.55] text-ink/80">{b.greeting}</p>
          </div>
          <button
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending}
            className="shrink-0 rounded-full bg-white/70 p-2 text-ink/55 backdrop-blur transition hover:text-ink disabled:opacity-50"
            aria-label={t('briefing.regenerate') as string}
            title={t('briefing.regenerate') as string}
          >
            {regenerate.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
          </button>
        </div>

        {b.actions.length === 0 ? (
          <div className="rounded-2xl bg-white/60 p-5 text-center">
            <p className="text-[14px] text-ink/65">{t('briefing.allClear')}</p>
            <button onClick={() => navigate('/audit')} className="btn-primary mt-4">
              <Sparkles size={15} /> {t('briefing.newAudit')}
            </button>
          </div>
        ) : (
          <div className={`grid items-stretch gap-3 ${compact ? '' : 'sm:grid-cols-3'}`}>
            {b.actions.map((action, i) => (
              <ActionCard
                key={i}
                action={action}
                index={i}
                onOpen={(a) => {
                  if (a.link) navigate(a.link);
                }}
              />
            ))}
          </div>
        )}

        {/* IneaMark retiré — la sidebar et la bulle assurent la présence d'Inea */}
      </div>
    </div>
  );
}

function ActionCard({
  action,
  index,
  onOpen,
}: {
  action: BriefingAction;
  index: number;
  onOpen: (a: BriefingAction) => void;
}) {
  const { t } = useTranslation();
  const Icon = ACTION_ICONS[action.type];
  const tint = ACTION_TINTS[action.type];

  return (
    <motion.button
      onClick={() => onOpen(action)}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.06 + index * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-ink/[0.08] bg-white/85 p-4 text-left backdrop-blur transition hover:border-pink/30 hover:shadow-[0_12px_30px_-16px_rgba(10,18,38,0.3)]"
    >
      <div
        className="pointer-events-none absolute -right-4 -top-4 h-16 w-16 rounded-full opacity-20 blur-2xl"
        style={{ background: tint }}
      />
      <div className="flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
          style={{
            background: `linear-gradient(135deg, ${tint}, #ec4899)`,
            boxShadow: `0 6px 14px -6px ${tint}`,
          }}
        >
          <Icon size={16} />
        </span>
        <div className="flex-1">
          <p className="font-mono text-[9.5px] font-700 uppercase tracking-[0.12em] text-ink/40">
            {index + 1} · {t(`briefing.actionTypes.${action.type}`)}
          </p>
          <p className="mt-0.5 font-display text-[14px] font-800 leading-[1.25] tracking-[-0.015em] text-ink">
            {action.title}
          </p>
        </div>
      </div>
      <p className="mt-2.5 line-clamp-3 text-[12.5px] leading-[1.5] text-ink/65">{action.detail}</p>
      {action.idea && (
        <p className="mt-2 line-clamp-2 rounded-lg bg-ink/[0.04] p-2 text-[12px] italic leading-[1.45] text-ink/70">
          « {action.idea} »
        </p>
      )}
      {action.link && (
        <span className="mt-auto pt-3 inline-flex items-center gap-1 text-[11.5px] font-700 text-pink transition-transform group-hover:translate-x-0.5">
          {t('briefing.openLabel')} <ArrowRight size={11} />
        </span>
      )}
    </motion.button>
  );
}

